import { App, Form, Select, Space, Typography } from 'antd';
import { useMemo, useState } from 'react';
import KPICard from '@/components/ui/analytics/KPICard';
import Table from '@/components/ui/table/Table';
import { DashboardContainer, KPICardsGrid, StudentsHeaderLeft, StudentsHeaderRow, StudentsHeaderSub, StudentsHeaderTitle } from '../style';
import { AppModal } from '@/components/ui/AppModal';
import { AppInput } from '@/components/ui/AppInput';
import { Button } from '@components/ui/Button';
import { formatMoney, parseMoneyNumber } from './shared';
import { createFinanceAccount, FINANCE_ACCOUNT_TYPES } from '@services/registry';
import { useAuthStore } from '@stores/auth.store';
import { useFinanceAccounts } from '@/hooks/tenant/finance/useFinanceAccounts';
import { useAbility } from '@/ability/AbilityProvider';

const { Text } = Typography;

export default function FinanceAccounts() {
    const { message } = App.useApp();
    const ability = useAbility();
    const tenantId = useAuthStore((s) => s.user?.tenantId);
    const canManage = !!ability?.can?.('manage', 'FinanceAccounts');
    const { rows: accounts, loading } = useFinanceAccounts({ tenantId });
    const [searchText, setSearchText] = useState('');
    const [createOpen, setCreateOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    const filteredRows = useMemo(() => {
        const q = (searchText || '').trim().toLowerCase();
        if (!q) return accounts || [];
        return (accounts || []).filter((a) => (a.name || '').toLowerCase().includes(q));
    }, [accounts, searchText]);

    const totals = useMemo(() => {
        const balance = (accounts || []).reduce((sum, a) => sum + (Number(a.balance) || 0), 0);
        const totalReceive = (accounts || []).reduce((sum, a) => sum + (Number(a.totalReceive) || 0), 0);
        const totalExpense = (accounts || []).reduce((sum, a) => sum + (Number(a.totalExpense) || 0), 0);
        return { balance, totalReceive, totalExpense };
    }, [accounts]);

    const columns = useMemo(() => ([
        { title: 'Account', dataIndex: 'name', key: 'name', render: (v) => v || '—' },
        { title: 'Type', dataIndex: 'type', key: 'type', render: (v) => v || '—' },
        {
            title: 'Total Receive',
            key: 'totalReceive',
            render: (_, row) => <Text style={{ color: '#16a34a' }}>{formatMoney(row.totalReceive || 0)}</Text>,
        },
        {
            title: 'Total Expense',
            key: 'totalExpense',
            render: (_, row) => <Text style={{ color: '#ef4444' }}>{formatMoney(row.totalExpense || 0)}</Text>,
        },
        { title: 'Balance', key: 'balance', render: (_, row) => <Text strong>{formatMoney(row.balance || 0)}</Text> },
    ]), []);

    const openCreate = () => {
        form.resetFields();
        form.setFieldsValue({ type: FINANCE_ACCOUNT_TYPES.COH, openingBalance: '' });
        setCreateOpen(true);
    };

    const onCreate = async () => {
        if (!tenantId) return;
        if (!canManage) {
            message.error('You have view-only access for Account Management.');
            return;
        }
        try {
            const v = await form.validateFields();
            setSaving(true);
            await createFinanceAccount({
                tenantId,
                name: (v.name || '').trim(),
                type: v.type,
                openingBalance: parseMoneyNumber(v.openingBalance),
            });
            message.success('Account created');
            setCreateOpen(false);
        } catch (e) {
            if (e?.errorFields) return;
            message.error(e?.message || 'Failed to create account');
        } finally {
            setSaving(false);
        }
    };

    return (
        <DashboardContainer>
            <StudentsHeaderRow>
                <StudentsHeaderLeft>
                    <StudentsHeaderTitle>Account Management</StudentsHeaderTitle>
                    <StudentsHeaderSub>Create COH/Bank accounts and track balances.</StudentsHeaderSub>
                </StudentsHeaderLeft>
                <Space size={10} wrap>
                    <Button type="primary" onClick={openCreate} disabled={!canManage}>
                        Create Account
                    </Button>
                </Space>
            </StudentsHeaderRow>

            <KPICardsGrid $columns={3} $rows={1} $height="auto" style={{ marginBottom: 16 }}>
                <KPICard title="Total Receive (All Accounts)" value={formatMoney(totals.totalReceive)} />
                <KPICard title="Total Expense (All Accounts)" value={formatMoney(totals.totalExpense)} />
                <KPICard title="Total Balance" value={formatMoney(totals.balance)} />
            </KPICardsGrid>

            <Table
                title="Accounts"
                columns={columns}
                dataSource={filteredRows}
                loading={loading}
                emptyText="No accounts yet"
                searchValue={searchText}
                onSearchChange={setSearchText}
                pagination={true}
                pageSize={10}
                showSearch={true}
                showPagination={true}
            />

            <AppModal
                title="Create account"
                open={createOpen}
                onCancel={() => setCreateOpen(false)}
                onOk={() => void onCreate()}
                okText="Save account"
                size="md"
                destroyOnClose
                okButtonProps={{ disabled: saving || !canManage }}
                confirmLoading={saving}
            >
                <Form form={form} layout="vertical" preserve={false}>
                    <Form.Item
                        name="name"
                        label="Account name"
                        rules={[{ required: true, message: 'Enter account name' }]}
                    >
                        <AppInput placeholder="e.g. Cash in Hand" />
                    </Form.Item>

                    <Form.Item
                        name="type"
                        label="Account type"
                        rules={[{ required: true, message: 'Select account type' }]}
                    >
                        <Select
                            options={[
                                { value: FINANCE_ACCOUNT_TYPES.COH, label: 'COH (Cash in Hand)' },
                                { value: FINANCE_ACCOUNT_TYPES.BANK, label: 'Bank' },
                            ]}
                        />
                    </Form.Item>

                    <Form.Item name="openingBalance" label="Opening balance">
                        <AppInput isPrice placeholder="0" />
                    </Form.Item>
                </Form>
            </AppModal>
        </DashboardContainer>
    );
}

